import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { StatusBadge, EmptyState } from './shared';
import { formatDate, getJobStatusColor, getStatusColor, stringToColor, getInitials } from '../helpers';
import { jobTypeName } from '../../../../utils/jobType';

// ---------------------------------------------------------------------------
// Internal primitives (only used in this file)
// ---------------------------------------------------------------------------
const ListItemBox = ({ accentColor, children }) => {
  const theme = useTheme();
  return (
    <ListItem
      sx={{
        py: 1.75,
        px: 1.5,
        borderRadius: 2,
        transition: 'background 0.15s',
        '&:hover': { bgcolor: alpha(accentColor, 0.04) },
      }}
    >
      {children}
    </ListItem>
  );
};

const PersonBadge = ({ user, roleLabel, roleColor }) => {
  const theme = useTheme();
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (!name) return null;
  const color = stringToColor(name);
  return (
    <Tooltip title={`${roleLabel}: ${name}`} placement="top">
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: alpha(color, 0.06),
          border: `1px solid ${alpha(color, 0.15)}`,
          cursor: 'default',
        }}
      >
        <Avatar
          sx={{
            width: 22,
            height: 22,
            fontSize: 9,
            fontWeight: 700,
            bgcolor: color,
            flexShrink: 0,
          }}
        >
          {getInitials(user.first_name, user.last_name)}
        </Avatar>
        <Box>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              color: roleColor || color,
              lineHeight: 1,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              mb: 0.2,
            }}
          >
            {roleLabel}
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1, color: 'text.primary' }}>
            {name}
          </Typography>
        </Box>
      </Stack>
    </Tooltip>
  );
};

const EntityIcon = ({ icon, color }) => (
  <Box
    sx={{
      width: 38,
      height: 38,
      borderRadius: 2,
      bgcolor: alpha(color, 0.1),
      border: `1px solid ${alpha(color, 0.15)}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color,
    }}
  >
    {icon}
  </Box>
);

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------
const ContractsList = ({ contracts }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!contracts?.length)
    return <EmptyState icon={<ArticleIcon sx={{ fontSize: 32 }} />} text={t('firmDetail.noContracts')} />;

  return (
    <List disablePadding>
      {contracts.map((contract, idx) => {
        const statusName = contract.work_status?.name;
        const statusColor = statusName ? getStatusColor(statusName, theme) : theme.palette.text.secondary;
        return (
          <React.Fragment key={contract.id}>
            {idx > 0 && <Divider component="li" sx={{ my: 0.25 }} />}
            <ListItemBox accentColor={theme.palette.primary.main}>
              <ListItemAvatar sx={{ minWidth: 46 }}>
                <EntityIcon icon={<ArticleIcon sx={{ fontSize: 18 }} />} color={theme.palette.primary.main} />
              </ListItemAvatar>
              <ListItemText
                disableTypography
                primary={
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    <Typography variant="body2" fontWeight={700}>
                      {contract.contract_subject?.name || '—'}
                    </Typography>
                    {statusName && <StatusBadge label={statusName} color={statusColor} />}
                    {contract.auto_renewal && (
                      <Chip
                       onClick={() => {
                    }}
                        label={t('firmDetail.autoRenewal', 'Oto. Yenileme')}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: alpha(theme.palette.success.main, 0.08), color: 'success.main', border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Stack>
                }
                secondary={
                  <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
                    {contract.scope_of_work?.name && (
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {contract.scope_of_work.name}
                      </Typography>
                    )}
                    {contract.pricing_method?.name && (
                      <Typography variant="caption" color="text.disabled">
                        · {contract.pricing_method.name}
                      </Typography>
                    )}
                    {contract.contract_signing_date && (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <CalendarTodayIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(contract.contract_signing_date)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                }
              />
            </ListItemBox>
          </React.Fragment>
        );
      })}
    </List>
  );
};

const OffersList = ({ offers }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!offers?.length)
    return <EmptyState icon={<LocalOfferIcon sx={{ fontSize: 32 }} />} text={t('firmDetail.noOffers')} />;

  return (
    <List disablePadding>
      {offers.map((offer, idx) => {
        const statusName = offer.proposal_status?.name;
        const statusColor = statusName ? getStatusColor(statusName, theme) : theme.palette.text.secondary;
        const creatorName = offer.user
          ? `${offer.user.first_name || ''} ${offer.user.last_name || ''}`.trim()
          : null;
        return (
          <React.Fragment key={offer.id}>
            {idx > 0 && <Divider component="li" sx={{ my: 0.25 }} />}
            <ListItemBox accentColor={theme.palette.warning.main}>
              <ListItemAvatar sx={{ minWidth: 46 }}>
                <EntityIcon icon={<LocalOfferIcon sx={{ fontSize: 18 }} />} color={theme.palette.warning.dark} />
              </ListItemAvatar>
              <ListItemText
                disableTypography
                primary={
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    <Typography variant="body2" fontWeight={700}>
                      {offer.contract_subject?.name || '—'}
                    </Typography>
                    {statusName && <StatusBadge label={statusName} color={statusColor} />}
                    {offer.proposal_given && (
                      <Chip
                       onClick={() => {
                    }}
                        label={t('firmDetail.proposalGiven', 'Teklif Verildi')}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: alpha(theme.palette.info.main, 0.08), color: 'info.main', border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Stack>
                }
                secondary={
                  <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
                    {offer.scope_of_work?.name && (
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {offer.scope_of_work.name}
                      </Typography>
                    )}
                    {offer.proposal_date && (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <CalendarTodayIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(offer.proposal_date)}
                        </Typography>
                      </Stack>
                    )}
                    {creatorName && (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <PersonIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          {creatorName}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                }
              />
            </ListItemBox>
          </React.Fragment>
        );
      })}
    </List>
  );
};

const JobsList = ({ jobs }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!jobs?.length)
    return <EmptyState icon={<WorkIcon sx={{ fontSize: 32 }} />} text={t('firmDetail.noJobs')} />;

  return (
    <Stack gap={1.25}>
      {jobs.map(job => {
        const statusColor = getJobStatusColor(job.job_status, theme);
        const techUser = job.job_assignment?.technical_user;
        const finUser = job.job_assignment?.financial_user;
        const creator = job.user;
        return (
          <Paper
            key={job.id}
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${alpha(statusColor, 0.2)}`,
              overflow: 'hidden',
              transition: 'box-shadow 0.18s, transform 0.18s',
              '&:hover': {
                boxShadow: `0 4px 16px ${alpha(statusColor, 0.15)}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box sx={{ height: 3, bgcolor: statusColor }} />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap" mb={(techUser || finUser || creator) ? 1 : 0}>
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    bgcolor: alpha(statusColor, 0.1),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: statusColor, flexShrink: 0,
                  }}
                >
                  <WorkIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box flex={1} minWidth={0}>
                  <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                    <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
                      {job.job_title || '—'}
                    </Typography>
                    {job.project_number && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700, fontFamily: 'monospace', fontSize: 10,
                          px: 0.75, py: 0.2, borderRadius: 1,
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          color: 'text.disabled',
                        }}
                      >
                        #{job.project_number}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={0.75} mt={0.5} flexWrap="wrap">
                    {job.job_status && <StatusBadge label={job.job_status} color={statusColor} />}
                    {jobTypeName(job) && (
                      <Chip
                       onClick={() => {
                    }}
                        label={jobTypeName(job)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>

              {(techUser || finUser || creator) && (
                <Box
                  sx={{
                    pt: 1,
                    borderTop: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
                    display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center',
                  }}
                >
                  {techUser && (
                    <PersonBadge
                      user={techUser}
                      roleLabel={t('firmDetail.technicalUser', 'Teknik')}
                      roleColor={theme.palette.info.main}
                    />
                  )}
                  {finUser && (
                    <PersonBadge
                      user={finUser}
                      roleLabel={t('firmDetail.financialUser', 'Mali')}
                      roleColor={theme.palette.success.dark}
                    />
                  )}
                  {!techUser && !finUser && creator && (
                    <PersonBadge
                      user={creator}
                      roleLabel={t('firmDetail.createdBy', 'Oluşturan')}
                      roleColor={theme.palette.text.secondary}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------
const TABS = ['contracts', 'offers', 'jobs'];
const TAB_ICONS = [
  <ArticleIcon sx={{ fontSize: 15 }} />,
  <LocalOfferIcon sx={{ fontSize: 15 }} />,
  <WorkIcon sx={{ fontSize: 15 }} />,
];

const FirmTabsPanel = ({ contracts, offers, jobs, activeTab, onTabChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const tabColors = [theme.palette.primary.main, '#f59e0b', theme.palette.secondary.main];
  const tabCounts = [contracts.length, offers.length, jobs.length];
  const tabContent = [
    <ContractsList key="c" contracts={contracts} />,
    <OffersList key="o" offers={offers} />,
    <JobsList key="j" jobs={jobs} />,
  ];

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => onTabChange(v)}
        sx={{
          bgcolor: alpha(theme.palette.text.primary, 0.015),
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiTab-root': {
            minHeight: 52,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.secondary',
            '&.Mui-selected': { color: tabColors[activeTab] },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            bgcolor: tabColors[activeTab],
            transition: 'background-color 0.3s',
          },
        }}
      >
        {TABS.map((tab, i) => (
          <Tab
            key={tab}
            label={
              <Stack direction="row" alignItems="center" gap={0.75}>
                {TAB_ICONS[i]}
                {t(`firmDetail.tabs.${tab}`)}
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: 1,
                    px: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: activeTab === i ? alpha(tabColors[i], 0.15) : alpha(theme.palette.text.primary, 0.07),
                    color: activeTab === i ? tabColors[i] : 'text.disabled',
                    transition: 'all 0.2s',
                  }}
                >
                  {tabCounts[i]}
                </Box>
              </Stack>
            }
          />
        ))}
      </Tabs>
      <Box
        sx={{
          p: 1.5,
          maxHeight: 705,
          minHeight: 705,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: alpha(theme.palette.text.primary, 0.1), borderRadius: 4 },
        }}
      >
        {tabContent[activeTab]}
      </Box>
    </Paper>
  );
};

export default FirmTabsPanel;
